import styles from "./Sidebar.module.css";

import { BrandLogo, ChevronLeftIcon, SettingsIcon } from "@/components/icons";
import { NavItem } from "@/components/NavItem";
import type { NavItem as NavItemType } from "@/types/navigation";

interface SidebarProps {
  items: readonly NavItemType[];
  activePath: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /**
   * When non-null, the Settings slot at the bottom renders a live nav link
   * to the given item (e.g. /settings/access for rbac.manage holders).
   * When null, the existing disabled "Coming soon" placeholder is shown.
   */
  settingsItem?: NavItemType | null;
}

/**
 * Primary navigation rail. Brand + collapse control on top, the feature nav in
 * the middle, and Settings pinned to the bottom. Nav entries come from the
 * central registry (app/navigation) so adding a feature adds a nav item without
 * touching this component.
 */
export function Sidebar({ items, collapsed, onToggleCollapsed, settingsItem }: SidebarProps) {
  const rootClass = collapsed ? `${styles.sidebar} ${styles.collapsed}` : styles.sidebar;

  return (
    <aside className={rootClass} aria-label="Primary">
      <div className={styles.top}>
        <div className={styles.brand}>
          <BrandLogo />
          <span className={styles.brandLabel}>
            <span className={styles.brandName}>IOG</span>
            <span className={styles.brandOrg}>Operating System</span>
          </span>
        </div>

        <button
          className={styles.collapse}
          aria-label={collapsed ? "Expand sidebar" : "Minimize sidebar"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand sidebar" : "Minimize sidebar"}
          onClick={onToggleCollapsed}
        >
          <ChevronLeftIcon />
        </button>
      </div>

      <nav className={styles.nav} aria-label="Sections">
        <ul>
          {items.map((item) => (
            <li key={item.id}>
              <NavItem to={item.path} label={item.label} icon={item.icon} collapsed={collapsed} />
            </li>
          ))}
        </ul>
      </nav>

      <div className={styles.settingsSlot}>
        {settingsItem != null ? (
          <NavItem
            to={settingsItem.path}
            label={settingsItem.label}
            icon={settingsItem.icon}
            collapsed={collapsed}
          />
        ) : (
          <div className={styles.settingsDisabled} aria-disabled="true" title="Coming soon">
            <NavItem
              label="Settings"
              icon={SettingsIcon}
              collapsed={collapsed}
              onClick={() => {
                // Intentionally disabled for now.
              }}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
